import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ImageWithWrap from '../../components/editor/ImageWithWrap';
import JotformEmbed from '../../components/editor/JotformEmbed';
import { addImageFromUrl, createImagePasteHandler } from '../../components/editor/editorImageAlt';
import { sanitizePostHtml, validateJotformInput } from '../../utils/jotformEmbed';
import { EditorImageAltControls } from '../../components/editor/EditorImageAltControls';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';
import { 
  ArrowLeft, Loader2, Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, Quote, Code, Image as ImageIcon, Link as LinkIcon, 
  Heading1, Heading2, Heading3, X, Plus, AlignLeft, AlignCenter, AlignRight, AlignJustify, WrapText, Square,
  Undo, Redo, Minus, Table as TableIcon, ClipboardList
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Editor styles
const editorStyles = `
  .ProseMirror {
    min-height: 400px;
    padding: 1rem;
    outline: none;
  }
  .ProseMirror > * + * {
    margin-top: 0.75em;
  }
  .ProseMirror h1 {
    font-size: 2em;
    font-weight: 700;
    line-height: 1.2;
    margin-top: 1.5em;
    margin-bottom: 0.5em;
  }
  .ProseMirror h2 {
    font-size: 1.5em;
    font-weight: 600;
    line-height: 1.3;
    margin-top: 1.25em;
    margin-bottom: 0.5em;
  }
  .ProseMirror h3 {
    font-size: 1.25em;
    font-weight: 600;
    line-height: 1.4;
    margin-top: 1em;
    margin-bottom: 0.5em;
  }
  .ProseMirror p {
    margin-bottom: 0.75em;
    line-height: 1.6;
  }
  .ProseMirror strong {
    font-weight: 700;
  }
  .ProseMirror em {
    font-style: italic;
  }
  .ProseMirror u {
    text-decoration: underline;
  }
  .ProseMirror s {
    text-decoration: line-through;
  }
  .ProseMirror ul {
    list-style-type: disc;
    padding-left: 1.5em;
    margin: 0.75em 0;
  }
  .ProseMirror ol {
    list-style-type: decimal;
    padding-left: 1.5em;
    margin: 0.75em 0;
  }
  .ProseMirror li {
    margin: 0.25em 0;
  }
  .ProseMirror blockquote {
    border-left: 4px solid #C8473A;
    padding-left: 1em;
    margin: 1em 0;
    font-style: italic;
    color: #555;
    background: #f9f9f9;
    padding: 0.75em 1em;
    border-radius: 0 0.5em 0.5em 0;
  }
  .ProseMirror code {
    background: #f4f4f4;
    padding: 0.2em 0.4em;
    border-radius: 4px;
    font-family: monospace;
    font-size: 0.9em;
  }
  .ProseMirror pre {
    background: #1e1e1e;
    color: #d4d4d4;
    padding: 1em;
    border-radius: 8px;
    overflow-x: auto;
    margin: 1em 0;
  }
  .ProseMirror pre code {
    background: none;
    padding: 0;
    color: inherit;
  }
  .ProseMirror img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 1em 0;
  }
  .ProseMirror p[style*="text-align: center"] img { display: block; margin-left: auto; margin-right: auto; }
  .ProseMirror p[style*="text-align: right"] img { display: block; margin-left: auto; }
  .ProseMirror [style*="text-align: justify"] { text-align: justify; }
  .ProseMirror img[data-float="left"] { float: left; margin: 0 1em 0.5em 0; max-width: 50%; }
  .ProseMirror img[data-float="right"] { float: right; margin: 0 0 0.5em 1em; max-width: 50%; }
  .ProseMirror img[data-float="block"],
  .ProseMirror img:not([data-float]) { float: none; display: block; }
  .ProseMirror a {
    color: #C8473A;
    text-decoration: underline;
    cursor: pointer;
  }
  .ProseMirror hr {
    border: none;
    border-top: 2px solid #e5e5e5;
    margin: 1.5em 0;
  }
  .ProseMirror table { border-collapse: collapse; width: 100%; margin: 1em 0; }
  .ProseMirror th, .ProseMirror td { border: 1px solid #e5e7eb; padding: 0.5em 0.75em; text-align: left; }
  .ProseMirror th { background: #f9fafb; font-weight: 600; }
  .ProseMirror p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: #adb5bd;
    pointer-events: none;
    height: 0;
  }
  .ProseMirror .is-editor-empty::before {
    content: attr(data-placeholder);
    color: #adb5bd;
    pointer-events: none;
    float: left;
    height: 0;
  }
  .ProseMirror .jotform-embed--editor {
    margin: 1em 0;
    user-select: none;
  }
  .ProseMirror .jotform-embed-placeholder {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
    border: 2px dashed rgba(200, 71, 58, 0.35);
    border-radius: 8px;
    background: #fafafa;
    color: #555;
    font-size: 0.95rem;
  }
  .ProseMirror .jotform-embed-placeholder__icon {
    font-size: 1.25rem;
    line-height: 1;
  }
  .ProseMirror .jotform-embed-placeholder__label {
    font-weight: 500;
    color: #211F1F;
  }
`;

const ToolbarButton = ({ onClick, isActive, disabled, children, title }) => (
  <Button
    type="button"
    variant={isActive ? 'secondary' : 'ghost'}
    size="sm"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className="h-8 w-8 p-0"
  >
    {children}
  </Button>
);

const ToolbarDivider = () => (
  <div className="w-px h-6 bg-gray-200 mx-1" />
);

const MenuBar = ({ editor, onUploadImageFromUrl }) => {
  if (!editor) return null;

  const addImage = () => addImageFromUrl(editor, onUploadImageFromUrl);

  const addLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl);
    
    if (url === null) return;
    
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const addJotformEmbed = () => {
    const input = window.prompt(
      'Paste the Jotform script tag or enter the form ID:',
      '',
    );
    if (input === null || input.trim() === '') return;

    const result = validateJotformInput(input);
    if (!result.valid) {
      toast.error(result.error);
      return;
    }

    editor.chain().focus().insertJotformEmbed(result.formId).run();
  };

  return (
    <div className="border-b bg-gray-50/50 p-2 flex flex-wrap items-center gap-0.5">
      {/* Undo/Redo */}
      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo (Ctrl+Z)"
      >
        <Undo className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo (Ctrl+Y)"
      >
        <Redo className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Headings */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        isActive={editor.isActive('heading', { level: 1 })}
        title="Heading 1"
      >
        <Heading1 className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={editor.isActive('heading', { level: 2 })}
        title="Heading 2"
      >
        <Heading2 className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        isActive={editor.isActive('heading', { level: 3 })}
        title="Heading 3"
      >
        <Heading3 className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Text Formatting */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        title="Bold (Ctrl+B)"
      >
        <Bold className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        title="Italic (Ctrl+I)"
      >
        <Italic className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={editor.isActive('underline')}
        title="Underline (Ctrl+U)"
      >
        <UnderlineIcon className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        isActive={editor.isActive('strike')}
        title="Strikethrough"
      >
        <Strikethrough className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Text Alignment */}
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        isActive={editor.isActive({ textAlign: 'left' })}
        title="Align Left"
      >
        <AlignLeft className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        isActive={editor.isActive({ textAlign: 'center' })}
        title="Align Center"
      >
        <AlignCenter className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        isActive={editor.isActive({ textAlign: 'right' })}
        title="Align Right"
      >
        <AlignRight className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('justify').run()}
        isActive={editor.isActive({ textAlign: 'justify' })}
        title="Justify (text wrap)"
      >
        <AlignJustify className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Lists */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        title="Bullet List"
      >
        <List className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        title="Numbered List"
      >
        <ListOrdered className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Block Elements */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive('blockquote')}
        title="Quote"
      >
        <Quote className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        isActive={editor.isActive('codeBlock')}
        title="Code Block"
      >
        <Code className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title="Horizontal Rule"
      >
        <Minus className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
        title="Insert table"
      >
        <TableIcon className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Media */}
      <ToolbarButton
        onClick={addImage}
        title="Insert Image"
      >
        <ImageIcon className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={addLink}
        isActive={editor.isActive('link')}
        title="Insert Link"
      >
        <LinkIcon className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={addJotformEmbed}
        isActive={editor.isActive('jotformEmbed')}
        title="Embed Jotform"
      >
        <ClipboardList className="w-4 h-4" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Wrap text around image (select an image first) */}
      <ToolbarButton
        onClick={() => editor.chain().focus().updateAttributes('image', { float: 'left' }).run()}
        isActive={editor.isActive('image') && editor.getAttributes('image').float === 'left'}
        title="Wrap text (image left)"
      >
        <WrapText className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().updateAttributes('image', { float: 'right' }).run()}
        isActive={editor.isActive('image') && editor.getAttributes('image').float === 'right'}
        title="Wrap text (image right)"
      >
        <WrapText className="w-4 h-4 rotate-180" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().updateAttributes('image', { float: null }).run()}
        isActive={editor.isActive('image') && !editor.getAttributes('image').float}
        title="No wrap (block)"
      >
        <Square className="w-4 h-4" />
      </ToolbarButton>

      <EditorImageAltControls editor={editor} />
    </div>
  );
};

const BlogForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [authors, setAuthors] = useState([]);
  const editorRef = React.useRef(null);
  const [formData, setFormData] = useState({ 
    title: '', 
    excerpt: '', 
    content: '', 
    featured_image_alt: '', 
    youtube_url: '',
    author_ids: [],
    published_at: new Date().toISOString(),
    tags: [], 
    category: 'general', 
    status: 'draft', 
    is_featured: false 
  });

  const uploadBlogImageFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await axios.post(`${API}/admin/blog/upload-image`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
    const url = data.url;
    return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  };

  const uploadBlogImageFromUrl = async (imageUrl) => {
    const { data } = await axios.post(`${API}/admin/blog/upload-image-from-url`, { url: imageUrl }, { withCredentials: true });
    const url = data.url;
    return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      ImageWithWrap.configure({
        HTMLAttributes: {
          class: 'rounded-lg max-w-full',
        },
      }),
      JotformEmbed,
      Link.configure({ 
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-[#C8473A] underline',
        },
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({
        placeholder: 'Start writing your blog post...',
      }),
    ],
    content: '',
    editorProps: {
      handlePaste: createImagePasteHandler(uploadBlogImageFile, () => editorRef.current),
      transformPastedHTML(html) {
        return sanitizePostHtml(html);
      },
    },
    onUpdate: ({ editor }) => {
      setFormData(prev => ({ ...prev, content: sanitizePostHtml(editor.getHTML()) }));
    },
  });

  React.useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // Fetch authors (team members) on mount
  useEffect(() => {
    axios.get(`${API}/admin/blog/authors`, { withCredentials: true })
      .then(res => setAuthors(res.data))
      .catch(() => console.error('Failed to load authors'));
  }, []);

  useEffect(() => {
    if (isEditing) {
      setLoading(true);
      axios.get(`${API}/admin/blog/${id}`, { withCredentials: true })
        .then(res => {
          const data = res.data;
          // Ensure author_ids is always an array (backwards compatibility)
          if (!data.author_ids) {
            data.author_ids = data.author_id ? [data.author_id] : [];
          }
          // Ensure tags is always an array
          if (!data.tags) {
            data.tags = [];
          }
          setFormData(data);
          if (editor && data.content) {
            editor.commands.setContent(sanitizePostHtml(data.content));
          }
          if (data.featured_image_url) {
            setImagePreview(
              data.featured_image_url.startsWith('/') 
                ? `${BACKEND_URL}${data.featured_image_url}` 
                : data.featured_image_url
            );
          }
        })
        .catch(() => toast.error('Failed to load post'))
        .finally(() => setLoading(false));
    }
  }, [id, isEditing, editor]);

  // Sync editor content when formData changes (for editing)
  useEffect(() => {
    if (editor && formData.content && editor.isEmpty) {
      editor.commands.setContent(sanitizePostHtml(formData.content));
    }
  }, [editor, formData.content]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (e) => { 
    const file = e.target.files[0]; 
    if (file) { 
      setImageFile(file); 
      setImagePreview(URL.createObjectURL(file)); 
    } 
  };
  
  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
      setTagInput('');
    }
  };
  
  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tagToRemove) }));
  };
  
  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title?.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        content: sanitizePostHtml(formData.content ?? ''),
        published_at: formData.published_at ? new Date(formData.published_at).toISOString() : null,
      };
      const res = isEditing
        ? await axios.put(`${API}/admin/blog/${id}`, payload, { withCredentials: true })
        : await axios.post(`${API}/admin/blog`, payload, { withCredentials: true });
      
      if (imageFile) {
        const fd = new FormData(); 
        fd.append('file', imageFile); 
        fd.append('alt_text', formData.featured_image_alt || formData.title);
        await axios.post(
          `${API}/admin/blog/${res.data.post_id}/featured-image`, 
          fd, 
          { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }
      toast.success(isEditing ? 'Post updated successfully' : 'Post created successfully');
      navigate('/admin/blog');
    } catch (error) { 
      toast.error(error.response?.data?.detail || 'Failed to save post'); 
    } finally { 
      setSaving(false); 
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#C8473A]" />
      </div>
    );
  }

  return (
    <div>
      {/* Inject editor styles */}
      <style>{editorStyles}</style>

      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/admin/blog')}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="text-2xl font-bold">{isEditing ? 'Edit Post' : 'New Blog Post'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg border p-6 space-y-4">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input 
                id="title"
                value={formData.title} 
                onChange={(e) => handleChange('title', e.target.value)} 
                className="text-lg font-medium mt-1" 
                placeholder="Enter post title..."
                required 
              />
            </div>
            
            <div>
              <Label htmlFor="excerpt">Excerpt</Label>
              <Textarea 
                id="excerpt"
                value={formData.excerpt} 
                onChange={(e) => handleChange('excerpt', e.target.value)} 
                rows={2} 
                placeholder="Brief summary of the post..." 
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Content *</Label>
              <div className="border rounded-lg mt-2 overflow-hidden bg-white">
                <MenuBar editor={editor} onUploadImageFromUrl={uploadBlogImageFromUrl} />
                <EditorContent 
                  editor={editor} 
                  className="min-h-[400px]"
                />
              </div>
              <div className="text-xs text-gray-500 mt-2 space-y-1">
                <p><strong>Shortcuts:</strong> Ctrl+B (bold), Ctrl+I (italic), Ctrl+U (underline)</p>
                <p><strong>Note:</strong> Headings apply to the entire paragraph. Place cursor on a line and click H1/H2/H3.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Post Settings */}
          <div className="bg-white rounded-lg border p-6 space-y-4">
            <h3 className="font-semibold text-sm text-gray-700">Post Settings</h3>
            
            <div>
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                <SelectTrigger id="status" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="published_at">Publish Date</Label>
              <Input 
                id="published_at"
                type="datetime-local"
                value={formData.published_at ? new Date(formData.published_at).toISOString().slice(0, 16) : ''}
                onChange={(e) => handleChange('published_at', e.target.value ? new Date(e.target.value).toISOString() : '')} 
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Defaults to current time when published</p>
            </div>
            
            <div>
              <Label htmlFor="category">Category</Label>
              <Input 
                id="category"
                value={formData.category} 
                onChange={(e) => handleChange('category', e.target.value)} 
                placeholder="e.g., insights, announcements" 
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Authors</Label>
              <div className="mt-2 space-y-2">
                {/* Selected Authors */}
                {(formData.author_ids?.length > 0) && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.author_ids.map((authorId) => {
                      const author = authors.find(a => a.member_id === authorId);
                      return author ? (
                        <Badge key={authorId} variant="secondary" className="gap-1 pr-1 py-1">
                          {author.photo_url && (
                            <img 
                              src={author.photo_url.startsWith('/') ? `${BACKEND_URL}${author.photo_url}` : author.photo_url}
                              alt={author.name}
                              className="w-4 h-4 rounded-full object-cover"
                            />
                          )}
                          {author.name}
                          <button 
                            type="button"
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              author_ids: (prev.author_ids || []).filter(id => id !== authorId)
                            }))}
                            className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
                
                {/* Author Selector */}
                <Select 
                  value="" 
                  onValueChange={(v) => {
                    if (v && !(formData.author_ids || []).includes(v)) {
                      setFormData(prev => ({
                        ...prev,
                        author_ids: [...(prev.author_ids || []), v]
                      }));
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Add an author..." />
                  </SelectTrigger>
                  <SelectContent>
                    {authors
                      .filter(a => !(formData.author_ids || []).includes(a.member_id))
                      .map((author) => (
                        <SelectItem key={author.member_id} value={author.member_id}>
                          <div className="flex items-center gap-2">
                            {author.photo_url && (
                              <img 
                                src={author.photo_url.startsWith('/') ? `${BACKEND_URL}${author.photo_url}` : author.photo_url}
                                alt={author.name}
                                className="w-5 h-5 rounded-full object-cover"
                              />
                            )}
                            {author.name}
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">Select multiple team members as authors</p>
              </div>
            </div>
            
            <div>
              <Label>Tags</Label>
              <div className="flex gap-2 mt-2">
                <Input 
                  value={tagInput} 
                  onChange={(e) => setTagInput(e.target.value)} 
                  onKeyDown={handleTagKeyDown}
                  placeholder="Add a tag..." 
                  className="flex-1"
                />
                <Button type="button" variant="outline" size="icon" onClick={handleAddTag}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {formData.tags.map((tag, idx) => (
                    <Badge key={idx} variant="secondary" className="gap-1 pr-1">
                      {tag}
                      <button 
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <Label htmlFor="featured" className="cursor-pointer">Featured Post</Label>
              <Switch 
                id="featured"
                checked={formData.is_featured} 
                onCheckedChange={(v) => handleChange('is_featured', v)} 
              />
            </div>
          </div>

          {/* Featured Image */}
          <div className="bg-white rounded-lg border p-6 space-y-4">
            <h3 className="font-semibold text-sm text-gray-700">Featured Image</h3>
            
            {imagePreview && (
              <img 
                src={imagePreview} 
                alt="Preview" 
                className="w-full h-40 object-cover rounded-lg" 
              />
            )}
            
            <Input 
              type="file" 
              accept="image/*" 
              onChange={handleImageChange} 
            />
            
            <Input 
              placeholder="Image alt text (for accessibility)" 
              value={formData.featured_image_alt} 
              onChange={(e) => handleChange('featured_image_alt', e.target.value)} 
            />
          </div>

          {/* YouTube Video */}
          <div className="bg-white rounded-lg border p-6 space-y-4">
            <h3 className="font-semibold text-sm text-gray-700">YouTube Video</h3>
            <p className="text-xs text-gray-500">Optional. Paste a YouTube URL; the video thumbnail will appear on the article and clicking it opens the embedded player.</p>
            <Input 
              placeholder="https://www.youtube.com/watch?v=..."
              value={formData.youtube_url || ''} 
              onChange={(e) => handleChange('youtube_url', e.target.value)} 
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate('/admin/blog')} 
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="flex-1 bg-[#C8473A] hover:bg-[#B74F40]" 
              disabled={saving}
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEditing ? 'Update Post' : 'Publish Post'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BlogForm;
